import {FC, useState} from 'react';
import dayjs from 'dayjs';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartOptions,
    ScaleOptions,
} from 'chart.js';

import {styled} from 'styled-components';
import {footer, generateChartData, yScale} from '../revenue/ui/chartConstants';
import {ILineChartCommonProps} from '../../../../types';
import {Line} from 'react-chartjs-2';
import {LegendRestContainer} from '../revenue/ui';
import {PuffLoader} from 'react-spinners';
import {cssOverride} from '../../../../utils/const';
import {colors} from '../../../../styles';
import {flexMixin} from '../../../../styles/mixins';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

export const LineChartRestByDays: FC<{
    chartData: ILineChartCommonProps | undefined;
}> = ({chartData}) => {
    const [hiddenDatasets, setHiddenDatasets] = useState<number[]>([]);

    if (!chartData) {
        return <PuffLoader color={colors.loader} cssOverride={cssOverride} />;
    }

    const {range, values} = chartData;

    const xScale: ScaleOptions<'category'> = {
        type: 'category',
        ticks: {
            callback: (value: string | number, index: number) => {
                if (typeof value === 'number') {
                    const rangeLength =
                        index % (Math.floor(range.length / 15) + 1);
                    return rangeLength === 0
                        ? dayjs(range[value]).format('DD.MM')
                        : '';
                }
                return value;
            },
        },
    };

    const datas = generateChartData(range, values, hiddenDatasets);

    const options: ChartOptions<'line'> = {
        maintainAspectRatio: false,
        responsive: true,
        interaction: {
            mode: 'index',
        },
        layout: {
            padding: {
                top: 10,
                bottom: 20,
            },
        },

        plugins: {
            tooltip: {
                mode: 'index' as 'index',
                intersect: true,
                usePointStyle: true,
                callbacks: {
                    footer,
                },
            },
            legend: {
                display: false,
            },
            title: {
                display: false,
                text: 'Chart.js Line Chart',
            },
        },
        elements: {
            line: {
                tension: 0.4,
                borderJoinStyle: 'round' as const,
            },
            point: {
                hoverRadius: 6,
                backgroundColor: '#ffffff',
                borderWidth: 1,
                hoverBorderWidth: 2,
            },
        },
        scales: {
            x: xScale,
            y: yScale,
        },
    };

    return (
        <ChartContentWrapper>
            <LegendContainer>
                <LegendRestContainer
                    datas={datas}
                    setHiddenDatasets={setHiddenDatasets}
                    hiddenDatasets={hiddenDatasets}
                />
            </LegendContainer>
            <ChartContainer>
                <Line options={options} data={datas} />
            </ChartContainer>
        </ChartContentWrapper>
    );
};
const ChartContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 500px;
    overflow: hidden;
`;

const LegendContainer = styled.div`
    flex-shrink: 0;
    margin-bottom: 20px;
    ${flexMixin('row', 'center', 'center')};
`;

const ChartContainer = styled.div`
    position: relative;
    flex: 1;
    min-height: 200px;
`;
